import axios from 'axios';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: process.env.NODE_ENV === 'production'
    ? '/api'
    : 'http://localhost:3000/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Reject a pending promise after `ms` so a single slow idToken() call can't block
// the whole request. clearTimeout avoids leaking the timer once it settles.
function withTimeout(promise, ms) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error('idToken timed out')), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

// Get a Shopify session token from App Bridge v4 (window.shopify.idToken).
// On first embedded load App Bridge connects to the Shopify host asynchronously, so an
// idToken() call made too early times out with "host did not respond in time". Retry
// with a short per-attempt cap until App Bridge is ready, rather than waiting out one
// long internal timeout. Returns null only if no token can be obtained at all.
async function getSessionToken() {
  if (typeof window === 'undefined' || !window.shopify || !window.shopify.idToken) {
    return null;
  }
  const maxAttempts = 6;
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      return await withTimeout(window.shopify.idToken(), 2000);
    } catch (err) {
      if (attempt === maxAttempts) {
        console.warn('Session token unavailable after retries:', err && err.message);
        return null;
      }
      await new Promise((resolve) => setTimeout(resolve, 250));
    }
  }
  return null;
}

// Request interceptor to add authentication.
// App Bridge v4 (loaded via the CDN script in public/index.html) exposes window.shopify.
// The backend verifies the session token and resolves the shop from its `dest` claim.
api.interceptors.request.use(
  async (config) => {
    const token = await getSessionToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Shop domain as a fallback identifier for the backend's no-bearer path.
    const shop =
      typeof window !== 'undefined' && window.shopify && window.shopify.config
        ? window.shopify.config.shop
        : null;
    if (shop) {
      config.headers['X-Shopify-Shop-Domain'] = shop;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      const { status, data } = error.response;

      switch (status) {
        case 401:
          // Unauthorized - redirect to auth
          console.error('Authentication required');
          // In a real app, you might redirect to the auth flow here
          break;
        case 403:
          // Forbidden
          console.error('Access denied');
          break;
        case 404:
          // Not found
          console.error('Resource not found');
          break;
        case 422:
          // Validation error
          console.error('Validation error:', data);
          break;
        case 500:
          // Server error
          console.error('Server error:', data);
          break;
        default:
          console.error('API Error:', error.response);
      }

      // Return the error response for handling in components
      return Promise.reject({
        status,
        message: data?.error || data?.message || 'An error occurred',
        data
      });
    } else if (error.request) {
      // The request was made but no response was received
      console.error('Network error:', error.request);
      return Promise.reject({
        status: 0,
        message: 'Network error - please check your connection',
        data: null
      });
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error('Request setup error:', error.message);
      return Promise.reject({
        status: -1,
        message: error.message || 'Request failed',
        data: null
      });
    }
  }
);

// Utility functions for common API operations
export const apiHelpers = {
  // Product limits
  getProductLimits: () => api.get('/products'),
  createProductLimit: (productId, data) => api.post(`/products/${productId}`, data),
  updateProductLimit: (productId, data) => api.put(`/products/${productId}`, data),
  deleteProductLimit: (productId, variantId) =>
    api.delete(`/products/${productId}${variantId ? `?variantId=${variantId}` : ''}`),

  // Customer management
  searchCustomers: (query, limit = 20) =>
    api.get(`/customers/search?query=${encodeURIComponent(query)}&limit=${limit}`),
  getCustomerPurchases: (customerId, productId, limit = 50) =>
    api.get(`/customers/${customerId}/purchases${productId ? `?productId=${productId}` : ''}${limit ? `&limit=${limit}` : ''}`),
  createCustomerOverride: (customerId, data) =>
    api.post(`/customers/${customerId}/override`, data),
  getCustomerOverrides: (customerId) =>
    api.get(`/customers/${customerId}/overrides`),
  removeCustomerOverride: (customerId, overrideId) =>
    api.delete(`/customers/${customerId}/override/${overrideId}`),

  // Limit checking
  checkPurchaseLimit: (data) => api.post('/limits/check', data),
  validateCart: (data) => api.post('/limits/validate-cart', data),
  getLimitStatus: (productId, variantId, customerId, customerEmail) => {
    const params = new URLSearchParams();
    if (variantId) params.append('variantId', variantId);
    if (customerId) params.append('customerId', customerId);
    if (customerEmail) params.append('customerEmail', customerEmail);

    return api.get(`/limits/status/${productId}?${params.toString()}`);
  },

  // Settings
  getSettings: () => api.get('/settings'),
  updateSettings: (settings) => api.post('/settings', { settings }),

  // Theme app embed (storefront enforcement) setup status + activation deep link
  getEmbedStatus: () => api.get('/settings/embed-status'),

  // Subscription status, and starting the Standard plan (returns Shopify's approval URL)
  getBillingStatus: () => api.get('/settings/billing'),
  startSubscription: () => api.post('/settings/billing/subscribe'),

  // Home dashboard aggregate stats
  getDashboardStats: () => api.get('/settings/dashboard-stats'),

  // Shop management
  getInstallStatus: (shop) => api.get(`/shopify/install-status/${shop}`),
  uninstallApp: (shop) => api.post('/shopify/uninstall', { shop })
};

export { api };
export default api;